import { useEffect, useState } from 'react';
import { api, type ControlSummary, type Framework } from './api.ts';
import './controls.css';

const SEVERITIES = ['critical', 'high', 'medium', 'low'] as const;
type SeverityFilter = (typeof SEVERITIES)[number] | 'all';
type View = 'controls' | 'frameworks';

const SEVERITY_LABEL: Record<string, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const label = (severity: string): string => SEVERITY_LABEL[severity] ?? severity;

/**
 * The severity badge, in the one markup shape the vocabulary defines: four
 * marks, the first N filled by CSS, and the word beside them. Written once here
 * so that the two places this screen shows a severity cannot disagree about how
 * many spans there are.
 */
const Sev = ({ severity }: { severity: string }) => {
  const known = (SEVERITIES as readonly string[]).includes(severity);
  return (
    <span className={known ? `sev sev-${severity}` : 'sev'}>
      <span className="sev-ticks" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
      </span>
      {label(severity)}
    </span>
  );
};

/**
 * Parameter defaults arrive as `unknown` because the catalogue allows numbers,
 * booleans and strings and the console must not assume which it was given by a
 * server it may be a version behind.
 */
const formatDefault = (value: unknown): string => {
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (value === null || value === undefined) return 'unset';
  if (typeof value === 'object') return JSON.stringify(value) ?? 'unset';
  return String(value);
};

const searchText = (control: ControlSummary): string =>
  [
    control.id,
    control.title,
    control.intent,
    control.severity,
    control.evidenceSource,
    ...control.mappings.map((mapping) => `${mapping.framework} ${mapping.criterion}`),
    ...Object.entries(control.parameters).map(([name, spec]) => `${name} ${spec.description}`),
    ...control.notes,
  ]
    .join(' ')
    .toLowerCase();

/** Groups in first-appearance order: the catalogue's ordering is deliberate. */
const groupsOf = (framework: Framework): Array<{ group: string; criteria: Framework['criteria'] }> =>
  framework.criteria
    .map((criterion) => criterion.group)
    .filter((group, index, all) => all.indexOf(group) === index)
    .map((group) => ({
      group,
      criteria: framework.criteria.filter((criterion) => criterion.group === group),
    }));

const ControlCard = ({
  control,
  criterionTitles,
}: {
  control: ControlSummary;
  criterionTitles: Record<string, string>;
}) => {
  const parameters = Object.entries(control.parameters);
  const attested = control.evidenceSource === 'attested';

  return (
    <article className="card control-card" aria-labelledby={`control-${control.id}`}>
      <div className="card-head">
        <div className="control-title">
          <span className="control-id mono">{control.id}</span>
          <h3 id={`control-${control.id}`}>{control.title}</h3>
        </div>
        <div className="control-marks">
          <Sev severity={control.severity} />
          {attested && <span className="attested" />}
        </div>
      </div>

      <div className="card-body">
        <p className="control-intent">{control.intent}</p>

        {attested && (
          <p className="callout callout-evidence control-attested">
            Part of the evidence for this control is a fact no API exposes, so the customer is asked
            and the finding records that it rests on their word.
          </p>
        )}

        <div className="chips">
          {control.mappings.length === 0 ? (
            <span className="chip chip-none">Maps to no framework criterion</span>
          ) : (
            control.mappings.map((mapping) => {
              const key = `${mapping.framework}:${mapping.criterion}`;
              return (
                <span className="chip" key={key} title={criterionTitles[key] ?? undefined}>
                  <span className="chip-framework">{mapping.framework}</span>
                  <span className="mono">{mapping.criterion}</span>
                </span>
              );
            })
          )}
        </div>

        <div className="control-detail">
          <section>
            <h4 className="label">Thresholds</h4>
            {parameters.length === 0 ? (
              <p className="note">Nothing configurable — the control is either met or it is not.</p>
            ) : (
              <dl className="params">
                {parameters.map(([name, spec]) => (
                  <div className="param" key={name}>
                    <dt>
                      <span className="mono param-name">{name}</span>
                      <span className="mono param-default">
                        {formatDefault(spec.default)}
                        {spec.unit ? ` ${spec.unit}` : ''}
                      </span>
                    </dt>
                    <dd className="note">{spec.description}</dd>
                  </div>
                ))}
              </dl>
            )}
          </section>

          <section>
            <h4 className="label">Notes</h4>
            {control.notes.length === 0 ? (
              <p className="note">None recorded.</p>
            ) : (
              <ul className="notes">
                {control.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </article>
  );
};

const FrameworkPanel = ({
  framework,
  byId,
}: {
  framework: Framework;
  byId: Record<string, ControlSummary>;
}) => {
  const covered = framework.criteria.filter((criterion) => criterion.controlIds.length > 0).length;
  const total = framework.criteria.length;
  const gaps = total - covered;

  return (
    <section className="card fw-card" aria-labelledby={`framework-${framework.id}`}>
      <div className="card-head">
        <div>
          <h3 id={`framework-${framework.id}`}>{framework.name}</h3>
          <p className="note fw-version">{framework.version}</p>
        </div>
        {/*
          Counts, never a proportion. "68% covered" is a number nobody can check
          and an invitation to chase it; "17 of 25 criteria, 8 with nothing
          mapped" is a list somebody can work through.
        */}
        <p className="note fw-coverage">
          <strong>
            <span className="num">{covered}</span> of <span className="num">{total}</span> criteria
          </strong>{' '}
          have at least one control in this catalogue
          {gaps > 0 ? (
            <>
              , and <span className="num">{gaps}</span> have none.
            </>
          ) : (
            '.'
          )}
        </p>
      </div>

      <div className="card-body">
        {/* Prominent and unskippable: a reader who takes a covered criterion for
            an audit opinion has been misled by this screen, not by their own
            optimism. Rendered verbatim — it is legal text. */}
        <p className="callout callout-caution fw-disclaimer">{framework.disclaimer}</p>

        {groupsOf(framework).map(({ group, criteria }) => (
          <div className="fw-group" key={group}>
            <h4 className="label">{group}</h4>
            {criteria.map((criterion) => {
              const controls = criterion.controlIds.map((id) => byId[id]);
              const uncovered = criterion.controlIds.length === 0;
              return (
                <div className={uncovered ? 'criterion criterion-gap' : 'criterion'} key={criterion.id}>
                  <p className="criterion-head">
                    <span className="criterion-id mono">{criterion.id}</span>
                    <span className="criterion-title">{criterion.title}</span>
                    {uncovered && <span className="gap-chip">No control mapped</span>}
                  </p>
                  {uncovered ? (
                    <p className="note">
                      No control in this catalogue evidences this criterion. That is coverage
                      information, not a finding about any client.
                    </p>
                  ) : (
                    <ul className="criterion-controls">
                      {criterion.controlIds.map((id, index) => {
                        const control = controls[index];
                        return (
                          <li key={id}>
                            <span className="control-id mono">{id}</span>
                            <span className="criterion-control-title">
                              {control ? control.title : 'Not in the published catalogue.'}
                            </span>
                            {control && <Sev severity={control.severity} />}
                            {control?.evidenceSource === 'attested' && (
                              <span className="attested" />
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </section>
  );
};

/**
 * The control catalogue and its framework projections.
 *
 * Roadmap §6 makes the mapping layer the bet that keeps "and can you also do
 * ISO 27001?" a data change: controls are authored once and projected into many
 * frameworks. This screen is where that becomes visible to a buyer — and where
 * the limits of it have to be equally visible, which is why uncovered criteria
 * are listed rather than filtered out and why every framework carries its
 * disclaimer at the top rather than in a footnote.
 *
 * Unlike the results components it fetches its own data: the catalogue is
 * static metadata with no tenant in it, so nothing upstream needs to own it.
 */
export const Controls = () => {
  const [controls, setControls] = useState<ControlSummary[]>([]);
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>('controls');
  const [query, setQuery] = useState('');
  const [severity, setSeverity] = useState<SeverityFilter>('all');

  useEffect(() => {
    let live = true;
    Promise.all([api.listControls(), api.listFrameworks()])
      .then(([controlResponse, frameworkResponse]) => {
        if (!live) return;
        setControls(controlResponse.controls);
        setFrameworks(frameworkResponse.frameworks);
      })
      .catch((cause: unknown) => {
        if (!live) return;
        setError(cause instanceof Error ? cause.message : String(cause));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  const term = query.trim().toLowerCase();
  const matches = controls.filter(
    (control) =>
      (severity === 'all' || control.severity === severity) &&
      (term === '' || searchText(control).includes(term)),
  );

  const byId: Record<string, ControlSummary> = Object.fromEntries(
    controls.map((control) => [control.id, control]),
  );
  const criterionTitles: Record<string, string> = Object.fromEntries(
    frameworks.flatMap((framework) =>
      framework.criteria.map((criterion) => [
        `${framework.id}:${criterion.id}`,
        `${framework.name} — ${criterion.title}`,
      ]),
    ),
  );
  const attested = controls.filter((control) => control.evidenceSource === 'attested').length;

  return (
    <div className="controls-screen">
      <p className="lead">
        One catalogue, projected into every framework it is mapped to. A control is authored once;
        the frameworks below are views over the same assessments, not separate control sets.
      </p>

      {error && <div className="error">{error}</div>}
      {loading && !error && <p className="note">Loading the catalogue…</p>}

      {!loading && !error && (
        <>
          <nav className="tabs-underline" aria-label="Catalogue views">
            <button
              className="tab"
              aria-current={view === 'controls' ? 'page' : undefined}
              onClick={() => setView('controls')}
            >
              By control
            </button>
            <button
              className="tab"
              aria-current={view === 'frameworks' ? 'page' : undefined}
              onClick={() => setView('frameworks')}
            >
              By framework
            </button>
          </nav>

          {view === 'controls' ? (
            <>
              <div className="card controls-toolbar">
                <div className="card-body">
                  <div className="field control-search">
                    <label htmlFor="control-search">
                      Search — title, intent, criterion, threshold or note
                    </label>
                    <input
                      id="control-search"
                      type="search"
                      value={query}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder="mfa, break-glass, CC6.1, dormant…"
                    />
                  </div>
                  <div className="sev-filters" role="group" aria-label="Filter by severity">
                    <button
                      className="btn btn-quiet btn-sm sev-filter"
                      aria-pressed={severity === 'all'}
                      onClick={() => setSeverity('all')}
                    >
                      <span className="sev-filter-all">All severities</span>
                      <span className="num sev-filter-count">{controls.length}</span>
                    </button>
                    {SEVERITIES.map((value) => {
                      const count = controls.filter((control) => control.severity === value).length;
                      return (
                        <button
                          key={value}
                          className="btn btn-quiet btn-sm sev-filter"
                          aria-pressed={severity === value}
                          disabled={count === 0}
                          onClick={() => setSeverity(value)}
                        >
                          <Sev severity={value} />
                          <span className="num sev-filter-count">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <p className="note" aria-live="polite">
                Showing <span className="num">{matches.length}</span> of{' '}
                <span className="num">{controls.length}</span> controls.
                {attested > 0 &&
                  ` ${attested} rest partly on a written attestation rather than on collected configuration, and are marked Attested — not collected.`}
              </p>

              {matches.length === 0 ? (
                <div className="empty">
                  <span className="empty-title">No control matches that search</span>
                  <p className="empty-body">
                    Try a control id, a framework criterion such as CC6.1, or a word from a
                    threshold description. Clearing the severity filter widens the search too.
                  </p>
                </div>
              ) : (
                matches.map((control) => (
                  <ControlCard key={control.id} control={control} criterionTitles={criterionTitles} />
                ))
              )}
            </>
          ) : (
            <>
              <p className="note fw-intro">
                Every criterion in each framework is listed, including those no control evidences. A
                coverage view that hides its gaps tells the reader the opposite of the truth.
              </p>
              {frameworks.map((framework) => (
                <FrameworkPanel key={framework.id} framework={framework} byId={byId} />
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
};
